import { diagnosisCaptureConfig } from '@muzly/shared';
import type { NativeRecording } from '@muzly/shared';

import { defined } from './toAnalySubmitBody';

import type { AnalyCapture } from './analyContract';

/**
 * toAnalyCapture — 네이티브 녹음(+게이트 바닥) → 서버 `capture` 모양.
 *
 * **명시로 옮긴다**(`toAnalySubmitBody`의 `toSongPartScore`와 같은 규칙) — recording을
 * 통째로 스프레드하면 앱 필드가 느는 날(`uri`·`sampleCount` 같은 것) 조용히 서버로
 * 새어 나간다. 필드 이름·의미는 서버 `AnalyCapture`(`analyContract.ts` 참고)가 정본이다.
 *
 * **없는 값은 키를 뺀다**(`NativeRecording` 주석의 optional 규칙). 예외는
 * `noiseFloorDbfs`뿐이다 — "게이트가 바닥을 못 잡았다"도 잰 사실이라 `null`로 싣는다.
 *
 * `recording === null`은 브라우저·mock 경로다 — 네이티브 녹음이 없으니 `capture` 키
 * 자체를 만들지 않는다(호출부가 `defined()`로 걷는다).
 */

/**
 * 라우트 raw 값은 항상 문자열로 보낸다. iOS는 포트 타입 문자열이지만 Android는
 * `AudioDeviceInfo.type` 정수라(`MuzlyPitchTrackerModule.kt`), 그대로 두면 한 필드에 두 타입이
 * 섞여 서버의 하위 진단 검색 색인이 깨질 수 있다. 이미 배포된 Android 빌드도 여기서 덮는다.
 * 없는 값(`undefined`·`null`)은 `undefined`로 남아 `defined()`가 키를 뺀다.
 */
const routeRaw = (value: string | number | undefined): string | undefined =>
  value == null ? undefined : String(value);

/** 서버가 받는 gap 오프셋 상한. 넘으면 앞쪽 64개만 남기고 `gapCount`는 계속 센다. */
export const MAX_GAP_OFFSETS = 64;

export const toAnalyCapture = (
  recording: NativeRecording | null,
  noiseFloorDbfs: number | null,
): AnalyCapture | undefined => {
  if (recording === null) {
    return undefined;
  }
  // 진단 캡처(`useVoiceCapture`)가 여는 config 그대로 — 거기서 바꾸는 것은 audio·output 뿐이다.
  const { silenceThresholdDb, confidenceThreshold, windowSize, hopSize } =
    diagnosisCaptureConfig().analysis;
  return {
    ...defined({
      platform: recording.platform,
      captureSource: recording.captureSource,
      sessionMode: recording.sessionMode,
      inputRoute: recording.inputRoute,
      inputRouteRaw: routeRaw(recording.inputRouteRaw),
      inputDataSource: recording.inputDataSource,
      backingTrack: recording.backingTrack,
      voiceProcessing: recording.voiceProcessing,
      agcEnabled: recording.agcEnabled,
      outputRouteRaw: routeRaw(recording.outputRouteRaw),
      ioBufferMs: recording.ioBufferMs,
      inputLatencyMs: recording.inputLatencyMs,
      outputLatencyMs: recording.outputLatencyMs,
      unprocessedSupported: recording.unprocessedSupported,
      sampleRate: recording.sampleRate,
      bitDepth: recording.bitDepth,
      durationSec: recording.durationSec,
      gapCount: recording.gapCount,
      gapOffsets: recording.gapOffsets?.slice(0, MAX_GAP_OFFSETS),
      clipSampleCount: recording.clipSampleCount,
      peakDbfs: recording.peakDbfs,
      degradedFrameCount: recording.degradedFrameCount,
      deviceModel: recording.deviceModel,
      osVersion: recording.osVersion,
      appVersion: recording.appVersion,
    }),
    silenceThresholdDb,
    confidenceThreshold,
    windowSize,
    hopSize,
    noiseFloorDbfs,
  };
};
